/**
 * @file: ArchivedSwipeableRow.tsx
 *
 * Wraps a feed {@link ThreadRow} in a single swipe action — restore
 * (un-archive). Mirrors the inbox swipe idiom but with one button, since
 * the archived view's only per-row action is putting the thread back.
 */

import { ThreadRow } from '@euler/app/flows/feed/components/ThreadRow';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { FC, memo, useCallback, useRef } from 'react';
import { Animated, Text, TouchableOpacity } from 'react-native';
import Swipeable from 'react-native-gesture-handler/Swipeable';

const ACTION_WIDTH = 88;
const RESTORE_COLOR = '#2a8c4a';

export const ArchivedSwipeableRow: FC<{
  thread: FeedThreadInboxItem;
  onPress: () => void;
  onRestore: (thread: FeedThreadInboxItem) => void;
}> = memo(({ thread, onPress, onRestore }) => {
  const { rpx } = useResponsiveContext();
  const ref = useRef<Swipeable>(null);

  const renderRightActions = useCallback(
    (progress: Animated.AnimatedInterpolation<number>) => {
      const translateX = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [rpx(ACTION_WIDTH), 0],
      });
      return (
        <Animated.View
          style={{ width: rpx(ACTION_WIDTH), transform: [{ translateX }] }}
        >
          <TouchableOpacity
            activeOpacity={0.7}
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: RESTORE_COLOR,
            }}
            onPress={() => {
              ref.current?.close();
              onRestore(thread);
            }}
          >
            <Text
              style={{ color: '#fff', fontSize: rpx(12) }}
              numberOfLines={1}
            >
              取消归档
            </Text>
          </TouchableOpacity>
        </Animated.View>
      );
    },
    [rpx, thread, onRestore],
  );

  return (
    <Swipeable
      ref={ref}
      renderRightActions={renderRightActions}
      overshootRight={false}
      containerStyle={{ backgroundColor: '#eeeef0' }}
    >
      <ThreadRow thread={thread} onPress={onPress} />
    </Swipeable>
  );
});
